import React, { useEffect, useCallback, useState, useRef } from "react";
import {
  Table,
  Typography,
  Select,
  Button,
  Avatar,
  Form,
  message,
  Modal,
  Input,
  Spin,
  Checkbox,
} from "antd";
import { DeleteOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import axios from "../../axios-interceptor.js";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { loadSpaceSpecificUser, loadSpaceSpecificGroups } from "../../redux/slices/userRoleGroupSlice.js";
import { fetchSpaceSpecificUsers } from "../../redux/actions/userRoleGroupAction.js";
import UserRoleDropdown from "../UserRoleDropdown/index.js";
import { showLoader } from "../../utils/showloader.js";
import { readMasterRestrictions } from "../../utils/masterRestrictions.js";
import {
  buildMasterOptionLabelMap,
  buildGlobalMasterLabelMap,
  resolveMasterValueLabel,
  mergeRowWithListTranslations,
  isUuidLike,
} from "../../utils/masterHelper.js";
import { DEFAULT_LANGUAGE, resolveRowTranslation } from "../../utils/masterTranslations.js";
import { getGroupsInSpace, getGroupUsers, removeUserFromSpace, removeGroupFromSpace } from "../../services/WebServices.js";
import { getUsersBySpace, addUserToSpace, getSingleMasterData } from "../../services/adminServices.js";
import { CheckCheck } from "lucide-react";

const BACKEND_API = process.env.REACT_APP_BACKEND_API;

// Mirrors the page size getUsersBySpace asks the backend for.
const SPACE_USERS_PAGE_SIZE = 50;

const isFilledText = (value) => value !== undefined && value !== null && value !== "" && typeof value !== "object"

// Display text for one cell of the restricting column. The column can hold plain
// text, an option id, or - when it points at another master - the referenced row's
// uuid, and only the first one is readable, so the other two go through the shared
// master label maps. Mirrors getLocalizedMasterCellText, with the maps hoisted out
// of the per row loop.
const readRestrictionLabel = (row, column, master, lng, columnLabelMap, globalLabelMap) => {
  const localizedRow = resolveRowTranslation(mergeRowWithListTranslations(row, master?.data), master?.fields, lng)
  const rawValue = row?.[column]
  const localizedValue = localizedRow?.[column]
  if (isFilledText(localizedValue) && String(localizedValue) !== String(rawValue) && !isUuidLike(String(localizedValue))) {
    return String(localizedValue)
  }
  const resolved = resolveMasterValueLabel(rawValue, columnLabelMap, globalLabelMap)
  return isFilledText(resolved) ? String(resolved) : ""
}

// Add responsive styles for better mobile experience
const addResponsiveStyles = () => {
  if (typeof document !== 'undefined' && !document.head.querySelector('style[data-responsive-members]')) {
    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-responsive-members', 'true');
    styleElement.textContent = `
      @media (max-width: 1024px) {
        .space-members-form .ant-form-item-label { padding-bottom: 4px; }
        .space-members-form .ant-row { gap: 12px; }
      }
      @media (max-width: 768px) {
        .users-table .ant-table-thead > tr > th { padding: 8px 6px; font-size: 13px; }
        .users-table .ant-table-tbody > tr > td { padding: 8px 6px; }
        .space-members-form { margin-bottom: 16px; }
      }
      @media (max-width: 640px) {
        .users-table .ant-table-container { overflow-x: auto; }
        .nested-table .ant-table-tbody > tr > td { padding: 6px 4px; }
      }
      
      /* Custom pagination styling */
      .custom-pagination {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 16px;
      }
      
      .users-table .ant-pagination {
        display: flex;
        align-items: center;
      }
      
      .users-table .ant-pagination-total-text {
        margin-right: auto;
        color: #666;
        font-size: 13px;
      }
      
      .users-table .ant-pagination-options {
        margin-left: 16px;
      }
      
      .users-table .ant-pagination-options-size-changer.ant-select {
        margin-right: 0;
      }
      
      .users-table .ant-table {
        border: 1px solid #f0f0f0;
        border-radius: 8px;
      }
      
      .users-table .ant-table-container {
        border-radius: 8px;
        overflow: hidden;
      }
      
      .users-table .ant-pagination-item {
        border-radius: 4px;
        min-width: 28px;
        height: 28px;
        line-height: 26px;
      }
      
      .users-table .ant-pagination-item-active {
        background-color: #1677ff;
        border-color: #1677ff;
      }
      
      .users-table .ant-pagination-item-active a {
        color: white;
      }
      
      .users-table .ant-select-selector {
        border-radius: 4px !important;
      }
    `;
    document.head.appendChild(styleElement);
  }
};

const Sharings = (props) => {
  const { userInfo, spaceEntitlements, download, handleDownload, spaceData, masterList = [] } = props;
  const { spaceId } = useParams();
  const [users, setUsers] = useState([]);
  const [form] = Form.useForm();
  const [isSearch, setIsSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [submittable, setSubmittable] = useState(false);
  const [rolesData, setRolesData] = useState(props.roles)
  const [groupMembersModal, setGroupMembersModal] = useState({ open: false, loading: false, members: [], group: null })
  const [selectedMasterData, setSelectedMasterData] = useState([])
  // One entry per distinct label of the restricting column, holding every row uuid
  // that carries that label. Access is still stored per row - that is what the
  // request filter matches on - while the dropdown only ever shows the label once.
  const [projectAccessGroups, setProjectAccessGroups] = useState([])
  const [pendingProjectAccess, setPendingProjectAccess] = useState({})
  const pendingProjectAccessRef = useRef({})
  const lastSavedProjectAccessRef = useRef({})
  const values = Form.useWatch([], form);
  const dispatch = useDispatch();

  const { t, i18n } = useTranslation();
  const contentLang = i18n.language || DEFAULT_LANGUAGE;
  const formValues = Form.useWatch([], form) || { allowedPerson: {} };

  // Add responsive styles
  React.useEffect(() => {
    addResponsiveStyles();
  }, []);

  // A space can restrict access along several master data at once. Spaces saved
  // before that held a single "<dataId>::<column>" string, which the reader below
  // normalises into a one-entry list.
  const masterRestrictions = React.useMemo(
    () => readMasterRestrictions(spaceData),
    [spaceData?.masterToRestrictAccess, spaceData?.masterColumnToRestrict]
  )

  useEffect(() => {
    let cancelled = false
    if (!masterRestrictions.length) {
      setSelectedMasterData([])
      setProjectAccessGroups([])
      return
    }
    const loadMasterRows = async () => {
      // masterList is fetched alongside the space, so it can land after spaceData
      // does - it has to stay in the deps below, or the first (empty) run would be
      // the only one. It also only carries the table rows some of the time, so when
      // they are missing they get pulled the same way the form dropdowns pull them.
      // Everything that is read lands in the same pool, which is what the label maps
      // resolve referenced rows against.
      const pool = Array.isArray(masterList) ? [...masterList] : []
      const readMaster = async (dataId) => {
        const listed = pool.find(master => master?.dataId === dataId)
        if (Array.isArray(listed?.data) && listed.data.length > 0) {
          return listed
        }
        const response = await getSingleMasterData(dataId)
        const fetched = response?.data
        if (!fetched?.dataId) {
          return listed
        }
        const index = pool.findIndex(master => master?.dataId === fetched.dataId)
        if (index >= 0) pool[index] = fetched
        else pool.push(fetched)
        return fetched
      }

      const masters = await Promise.all(masterRestrictions.map(restriction => readMaster(restriction.dataId)))
      // The restricting column can itself point at another master, in which case the
      // cell holds that row's uuid and the referenced table is what carries its label.
      await Promise.all(masters.map((master, index) => {
        const columnField = (master?.fields || []).find(field => field?.label === masterRestrictions[index].column)
        return columnField?.parentTable ? readMaster(columnField.parentTable) : null
      }))
      if (cancelled) return

      const accessGroups = []
      const groups = masters.map((master, index) => {
        const restriction = masterRestrictions[index]
        const rows = Array.isArray(master?.data) ? master.data : []
        const columnField = (master?.fields || []).find(field => field?.label === restriction.column)
        const columnLabelMap = buildMasterOptionLabelMap(columnField, pool, contentLang)
        const globalLabelMap = buildGlobalMasterLabelMap(pool, contentLang)
        const isReferenceColumn = !!columnField?.parentTable
        const byLabel = new Map()
        rows.forEach(row => {
          if (!row?.uuid) return
          const label = readRestrictionLabel(row, restriction.column, master, contentLang, columnLabelMap, globalLabelMap)
          if (!label) return
          // A form answers a column that points at another master with the id that
          // column holds, so that is the id a request carries and the one access is
          // keyed by. The row uuids ride along: answers stored before the column was
          // keyed that way hold those instead, and both have to keep matching.
          const rawCell = row[restriction.column]
          const referencedId = (isReferenceColumn && rawCell !== undefined && rawCell !== null && rawCell !== "")
            ? String(rawCell)
            : ""
          const rowUuid = String(row.uuid)
          const group = byLabel.get(label)
          if (group) {
            if (!group.values.includes(rowUuid)) group.values.push(rowUuid)
            if (referencedId && !group.values.includes(referencedId)) group.values.push(referencedId)
            return
          }
          byLabel.set(label, {
            label,
            value: referencedId || rowUuid,
            values: referencedId ? [referencedId, rowUuid] : [rowUuid]
          })
        })
        const entries = [...byLabel.values()]
        accessGroups.push(...entries)
        return {
          label: master?.dataName || restriction.dataId,
          options: entries.map(entry => ({ label: entry.label, value: entry.value }))
        }
      })
      // Values stay a flat list of row uuids, so the grouping is display-only.
      setProjectAccessGroups(accessGroups)
      setSelectedMasterData(groups.filter(group => group.options.length > 0))
    }
    loadMasterRows()
    return () => { cancelled = true }
  }, [masterRestrictions, masterList, contentLang])

  // Flat uuid -> label lookup across every group, for rendering stored values.
  // Keyed off every row behind a label, not just the one the dropdown offers.
  const masterDataLabels = React.useMemo(() => {
    const labels = {}
    projectAccessGroups.forEach(group => {
      group.values.forEach(value => { labels[value] = group.label })
    })
    return labels
  }, [projectAccessGroups])

  // Calculate statistics
  const statistics = React.useMemo(() => {
    const individualUsers = users.filter(user => !user.groupId);
    const groups = users.filter(user => user.groupId);
    const totalMembers = individualUsers.length + groups.reduce((acc, group) => {
      // This will be updated when we fetch group members
      return acc + (group.memberCount || 0);
    }, 0);

    return {
      individualUsers: individualUsers.length,
      groups: groups.length,
      totalMembers: totalMembers
    };
  }, [users]);

  // Callback to update member count for groups
  const updateGroupMemberCount = useCallback((groupId, count) => {
    setUsers(prevUsers => prevUsers.map(user => user.groupId === groupId ? { ...user, memberCount: count } : user))
  }, [])

  useEffect(() => {
    if (props.roles) {
      setRolesData(props.roles)
    }
  }, [props.roles])

  // Members are already loaded here from the same two endpoints the
  // userRoleGroupState thunks use, so the store is fed from this response
  // instead of refetching it after every add/remove/role change. The user list
  // comes back paginated, so if the page is full the store could be missing
  // members and the thunk is left to pull the whole set.
  const syncUserGroupStore = useCallback((spaceUsers = [], spaceGroups = []) => {
    dispatch(loadSpaceSpecificGroups({ data: spaceGroups }))
    if (spaceUsers.length >= SPACE_USERS_PAGE_SIZE) {
      dispatch(fetchSpaceSpecificUsers({ spaceId, userInfo: { userId: userInfo.userId } }, true))
      return
    }
    dispatch(loadSpaceSpecificUser({ data: spaceUsers }))
  }, [dispatch, spaceId, userInfo.userId]);

  const fetchUsers = useCallback(async () => {
    showLoader(true)
    let spaceUsers = await getUsersBySpace(spaceId)
    let allUsers = spaceUsers.map((user) => {
      return {
        ...user
      };
    });

    let groupList = await getGroupsInSpace(spaceId, userInfo.userId)
    showLoader(false)
    let spaceGroups = groupList.data?.result || []
    let groupData = spaceGroups.map(group => {
      let groupRole = group.groupAccess?.find(grp => grp.resource === spaceId)
      return {
        ...group,
        role: groupRole?.role,
        memberCount: group?.users?.length || 0 // Add member count from group data
      }
    })
    allUsers = allUsers.concat(groupData)
    setUsers(allUsers)
    syncUserGroupStore(spaceUsers, spaceGroups)
  }, [spaceId, userInfo.userId, syncUserGroupStore]);

  const addUser = async (user) => {
    const currentFormValues = form.getFieldsValue(true) || {};
    const restrictedUser =
      typeof user?.restrictedUser === "boolean"
        ? user.restrictedUser
        : !!currentFormValues.restrictedUser;
    // Duplicate validation (by email for users & by groupId for groups)
    const selectedUserEmails = (user?.allowedPerson?.shareWithUsers || []).map(e => e?.toLowerCase?.());
    const selectedGroupIds = user?.allowedPerson?.shareWithGroups || [];
    const existingUserEmails = users.filter(u => !u.groupId).map(u => u.email?.toLowerCase?.());
    const existingGroupIds = users.filter(u => u.groupId).map(g => g.groupId);

    const duplicateUserEmails = selectedUserEmails.filter(e => existingUserEmails.includes(e));
    const duplicateGroupIds = selectedGroupIds.filter(id => existingGroupIds.includes(id));

    // If everything selected is already present, block the call entirely
    if (
      (duplicateUserEmails.length === selectedUserEmails.length) &&
      (duplicateGroupIds.length === selectedGroupIds.length) &&
      (selectedUserEmails.length + selectedGroupIds.length) > 0
    ) {
      if (duplicateUserEmails.length > 0) {
        message.warning(t("User already exists in space") + (duplicateUserEmails.length > 1 ? `: ${duplicateUserEmails.join(', ')}` : ''));
      } else if (duplicateGroupIds.length > 0) {
        message.warning(t("Group already exists in space"));
      }
      return; // Do not proceed
    }

    // Filter out duplicates (allow adding only new ones) if there is a mix
    const filteredUserEmails = selectedUserEmails.filter(e => !duplicateUserEmails.includes(e));
    const filteredGroupIds = selectedGroupIds.filter(id => !duplicateGroupIds.includes(id));

    if (duplicateUserEmails.length > 0) {
      message.info(t("Skipping already added users") + `: ${duplicateUserEmails.join(', ')}`);
    }
    if (duplicateGroupIds.length > 0) {
      message.info(t("Skipping already added groups"));
    }

    // If after filtering nothing is left to add, stop
    if (filteredUserEmails.length === 0 && filteredGroupIds.length === 0) {
      return;
    }

    showLoader(true)
    let addResponse = await addUserToSpace(
      {
        spaceId: spaceId,
        groupId: filteredGroupIds,
        userIds: filteredUserEmails,
        role: user.role,
        projectLevelAccess: expandProjectAccess(user.projectLevelAccess || []),
        restrictedUser: restrictedUser
      },
      userInfo.userId
    )
    showLoader(false)
    if (addResponse.status === 200) {
      form.resetFields();
      message.success(t("User added to space successfully"))
      await fetchUsers();
    } else {
      message.error(addResponse.message)
    }
  };
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => {
        if (userInfo?.isAdmin || spaceEntitlements.includes("Space_Sharing_Update")) {
          if (values.allowedPerson && (values.allowedPerson.shareWithGroups.length > 0 || values.allowedPerson.shareWithUsers.length > 0)) {
            setSubmittable(true)

          } else {
            setSubmittable(false)
          }
        }
      })
      .catch(() => setSubmittable(false));
  }, [form, values, spaceEntitlements, userInfo?.isAdmin])

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);


  useEffect(() => {
    if (download) {
      handleDownload(users)
    }
  }, [download])

  const removeUser = async (user) => {
    // return
    let response = {}
    showLoader(true)
    if (user.groupId) {
      response = await removeGroupFromSpace(spaceId, user.groupId)
    }
    else {
      response = await removeUserFromSpace(spaceId, user.userId, userInfo.userId)
    }
    showLoader(false)
    if (!response.success) {
      message.error(t(response.error))
      return
    }
    message.success(t(`${user.userId ? "User" : "Group"} removed from the space successfully`));
    await fetchUsers();

  };

  const updateUserRole = async ({ userId, email, role, projectLevelAccess = [], groupId = undefined }) => {
    // let currentUser =  
    let postData = {
      spaceId: spaceId,
      role: role,
      projectLevelAccess: projectLevelAccess,
      groupId: [],
      userIds: []
    }
    if (userId) {
      postData.userIds = [email]
    } else if (groupId) {
      postData.groupId = [groupId]
    }
    try {
      showLoader(true)
      await axios({
        method: "PUT",
        url: `${BACKEND_API}/users`,
        data: postData,
        headers: {
          Accept: "application/json",
          userId: userInfo.userId,
          tenantId: 1
        },
      });
      showLoader(true)

      message.success(t("User role updated successfully"));
      fetchUsers();
      return true;
    } catch (error) {
      showLoader(false)
      message.error(t("Failed to update user"));
      return false;
    }
  };

  const getRowKey = useCallback((record) => record.groupId || record.userId || record.email, []);

  const areProjectAccessEqual = useCallback((a = [], b = []) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    for (let i = 0; i < sa.length; i += 1) {
      if (sa[i] !== sb[i]) return false;
    }
    return true;
  }, []);

  // Access is stored as the id a request actually carries - one per label, the id
  // the referenced row is known by - while a saved list can still hold any of the
  // ids that label was written under before, so reading one back finds its group.
  const collapseProjectAccess = useCallback((values) => {
    if (!Array.isArray(values)) return []
    const collapsed = []
    values.forEach(value => {
      const group = projectAccessGroups.find(item => item.values.includes(value))
      const entry = group ? group.value : value
      if (!collapsed.includes(entry)) collapsed.push(entry)
    })
    return collapsed
  }, [projectAccessGroups]);

  // One id per picked label. Writing out every row behind the label instead would
  // put a few thousand uuids on a user for a single pick, which the 5KB cap on
  // `projectLevelAccess` rejects; matching answers stored under an older id is the
  // query's job, not something each user record should carry.
  const expandProjectAccess = useCallback((values) => {
    if (!Array.isArray(values)) return []
    const stored = []
    values.forEach(value => {
      const group = projectAccessGroups.find(item => item.value === value || item.values.includes(value))
      const entry = group ? group.value : value
      if (!stored.includes(entry)) stored.push(entry)
    })
    return stored
  }, [projectAccessGroups]);

  const handleProjectAccessChange = useCallback((rowKey, nextValue) => {
    pendingProjectAccessRef.current[rowKey] = nextValue;
    setPendingProjectAccess(prev => ({ ...prev, [rowKey]: nextValue }));
  }, []);

  // Leaving the field saves it, whether it was left by closing the list or by
  // clicking away after taking the last tag off. Both can happen one after the
  // other, so what was last sent is remembered and only a real change goes out.
  const commitProjectAccess = useCallback(async (row, rowKey) => {
    const nextValue = pendingProjectAccessRef.current[rowKey] ?? pendingProjectAccess[rowKey] ?? row.projectLevelAccess;
    if (!nextValue) return;
    const savedValue = lastSavedProjectAccessRef.current[rowKey] ?? row.projectLevelAccess ?? [];
    if (areProjectAccessEqual(nextValue, savedValue)) return;
    lastSavedProjectAccessRef.current[rowKey] = nextValue;
    const saved = await updateUserRole({ ...row, projectLevelAccess: nextValue });
    if (!saved) delete lastSavedProjectAccessRef.current[rowKey];
  }, [areProjectAccessEqual, pendingProjectAccess, updateUserRole]);
  const openGroupMembers = async (groupRecord) => {
    if (!groupRecord?.groupId) return;
    setGroupMembersModal({ open: true, loading: true, members: [], group: groupRecord })
    try {
      const userList = await getGroupUsers(groupRecord.groupId, userInfo)
      if (userList?.data?.result) {
        setGroupMembersModal(prev => ({ ...prev, members: userList.data.result, loading: false }))
        updateGroupMemberCount(groupRecord.groupId, userList.data.result.length)
      } else {
        setGroupMembersModal(prev => ({ ...prev, loading: false }))
      }
    } catch (err) {
      setGroupMembersModal(prev => ({ ...prev, loading: false }))
    }
  }

  const getProjectLevelAccess = (values) => {
    if (values.length === 0) {
      return "-"
    }
    // masterDataLabels already pairs every row uuid with its label from the
    // restricting column, so the lookup reuses it instead of re-reading masterList,
    // which is not guaranteed to hold the rows.
    // Several rows can share a label, so the same one is only listed once.
    const accessLabels = []
    values.forEach(value => {
      const label = masterDataLabels[value]
      if (label && !accessLabels.includes(label)) accessLabels.push(label)
    })
    return accessLabels.length > 0 ? accessLabels.join(", ") : "-"
  }
  const columns = [
    {
      title: t("User/Group"),
      dataIndex: "name",
      key: "1",
      width: "40%",
      render: (value, record) => {
        return (
          <div className={`flex items-center gap-3 min-w-0 ${record.groupId ? 'cursor-pointer' : ''}`}
            onClick={() => openGroupMembers(record)}
          >
            <Avatar
              className="flex-shrink-0"
              style={{
                color: record.groupId ? "#52C41A" : "#1677FF",
                backgroundColor: record.groupId ? "#F6FFED" : "#F0F5FF",
              }}
              icon={<UserOutlined />}
            />
            <div className="min-w-0 flex-1">
              <span className="truncate font-medium block">{value}</span>
              {record.groupId && (
                <span className="text-xs text-gray-500">
                  {record.memberCount || 0} {t("members")}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: t("Email/Type"),
      dataIndex: "email",
      key: "2",
      width: "35%",
      render: (value, row) => {
        if (row.groupId) {
          return (
            <span className="text-blue-600 cursor-pointer text-sm" onClick={() => openGroupMembers(row)}>{(row.memberCount || 0)} {t('Members')}</span>
          )
        }
        return <span className="text-gray-600 truncate block">{value}</span>
      }
    },
    {
      title: t("Role"),
      dataIndex: "role",
      key: "3",
      width: "20%",
      render: (value, row) => {
        return (
          <>
            {
              (userInfo.isAdmin || spaceEntitlements.includes("Space_Sharing_Update")) ?
                <Select
                  value={value}
                  className="w-full max-w-[140px]"
                  options={rolesData}
                  onChange={(nextValue) => {
                    if (nextValue === value) return;
                    updateUserRole({ ...row, role: nextValue });
                  }}
                />
                : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {value}
                </span>
            }
          </>
        );
      },
    }
  ];
  if (masterRestrictions.length > 0 && (userInfo.isAdmin || spaceEntitlements.includes('Space_Sharing_Delete'))) {
    columns.push({
      title: t("Project Level Access"),
      dataIndex: "projectLevelAccess",
      key: "projectLevelAccess",
      width: "20%",
      render: (value, row) => {
        const rowKey = row.groupId || row.userId || row.email;
        const displayValue = pendingProjectAccess[rowKey] ?? value;
        return (
          <>
            {
              (userInfo.isAdmin || spaceEntitlements.includes("Space_Sharing_Update")) ?
                <Select
                  value={collapseProjectAccess(displayValue)}
                  mode="multiple"
                  maxTagCount="responsive"
                  className="w-full max-w-[200px]"
                  options={selectedMasterData}
                  onChange={(nextValue) => handleProjectAccessChange(rowKey, expandProjectAccess(nextValue))}
                  onDropdownVisibleChange={(open) => {
                    if (!open) {
                      commitProjectAccess(row, rowKey);
                    }
                  }}
                  onBlur={() => commitProjectAccess(row, rowKey)}
                  onClick={(e) => e.stopPropagation()}
                  // The list is left on the default container. Anchored to the cell it
                  // sits in, it is placed against an element that is not a positioning
                  // context and is clipped by the table's own scrolling, which put it
                  // over the header once the rows were filtered by a search.
                />
                : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {getProjectLevelAccess(value)}
                </span>
            }
          </>
        );
      },
    }

    )
  }
  if (userInfo.isAdmin || spaceEntitlements.includes('Space_Sharing_Delete')) {
    columns.push({
      title: t("Action"),
      dataIndex: "action",
      key: "4",
      width: "5%",
      align: "center",
      render: (value, row) => {
        return (
          <Button
            type="text"
            size="small"
            danger
            onClick={() => {
              Modal.confirm({
                title: t(row.groupId ? 'Remove group' : 'Remove member'),
                content: t(row.groupId ? 'Are you sure you want to remove this group from the space?' : 'Are you sure you want to remove this member from the space?'),
                okText: t("Remove"),
                cancelText: t("Cancel"),
                okType: "danger",
                onOk: (event) => {
                  removeUser(row)
                  Modal.destroyAll()
                },
                footer: (_, { OkBtn, CancelBtn }) => (
                  <>
                    <CancelBtn />
                    <OkBtn />
                  </>
                ),
              });
            }}
            icon={<DeleteOutlined />}
            className="hover:bg-red-50"
          />
        );
      },
    })
  }

  const filteredUsers = React.useMemo(() => {
    if (!isSearch) return users;
    const term = isSearch.toLowerCase();
    return users.filter((user) =>
      user.email?.toLowerCase().includes(term) ||
      user.name?.toLowerCase().includes(term)
    );
  }, [users, isSearch]);

  // Reset to the first page whenever the search term changes so we never
  // land on a now-empty page.
  useEffect(() => {
    setCurrentPage(1);
  }, [isSearch]);

  const onSelect = (values) => {
    let finalResult = {
      // ...formValues.allowedPerson,  
      "shareWithRoles": [],
      "shareWithUsers": values.user,
      "shareWithGroups": values.group
    }
    form.setFieldValue("allowedPerson", finalResult)
  }
  if (values?.user?.length === 0 && values?.group?.length === 0) {
    form.setFieldValue("role", "")
  }

  return (
    <div className="bg-white  rounded-2xl">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex-1">
          <Typography.Title level={4} className="!mb-4 !mt-0">
            {t("Space Members")}
          </Typography.Title>

          {/* Statistics Cards */}
          {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 px-4 py-3 rounded-lg text-center">
              <div className="text-2xl font-semibold text-blue-600">{statistics.individualUsers}</div>
              <div className="text-sm text-blue-700 mt-1">{t("Individual Users")}</div>
            </div>
            <div className="bg-green-50 px-4 py-3 rounded-lg text-center">
              <div className="text-2xl font-semibold text-green-600">{statistics.groups}</div>
              <div className="text-sm text-green-700 mt-1">{t("Groups")}</div>
            </div>
            <div className="bg-purple-50 px-4 py-3 rounded-lg text-center">
              <div className="text-2xl font-semibold text-purple-600">{statistics.totalMembers}</div>
              <div className="text-sm text-purple-700 mt-1">{t("Total Members")}</div>
            </div>
          </div> */}

          {/* Add User Form - Responsive Layout */}
          {(userInfo.isAdmin || spaceEntitlements.includes('Space_Sharing_Create')) && (
            <Form
              form={form}
              name="user-add-form"
              layout="vertical"
              onFinish={addUser}
              className="space-members-form"
            >
              <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
                <Form.Item
                  name="allowedPerson"
                  // label={t("Select Users/Groups")}
                  rules={[{ required: true, message: t("Please select users or groups") }]}
                  className="lg:w-[240px] !mb-0"
                >
                  <UserRoleDropdown
                    showRole={false}
                    placeholder={t("Select user or group")}
                    selectedOptions={formValues.allowedPerson ? {
                      "user": formValues.allowedPerson.shareWithUsers || [],
                      "role": formValues.allowedPerson.shareWithRoles || [],
                      "group": formValues.allowedPerson.shareWithGroups || []
                    } : { user: [], role: [], group: [] }}
                    onSelect={onSelect}
                    tabsRequired={["user", "group"]}
                    getAllGroups={true}
                  />
                </Form.Item>

                <Form.Item
                  name="role"
                  // label={t("Role")}
                  rules={[{ required: true, message: t("Please select a role") }]}
                  className="lg:w-48 !mb-0"
                >
                  <Select disabled={(formValues?.allowedPerson && (formValues?.allowedPerson?.shareWithUsers?.length > 0 || formValues?.allowedPerson?.shareWithGroups?.length > 0)) ? false : true}
                    placeholder={t("Select role")}
                    options={rolesData}
                  />
                </Form.Item>

                {
                  masterRestrictions.length > 0 && (
                    <Form.Item
                      name="projectLevelAccess"

                      // label={t("Role")}
                      // rules={[{ required: true, message: t("Please select a role") }]}
                      className="lg:w-48 !mb-0"
                    >
                      <Select
                        mode="multiple"
                        maxTagCount="responsive"
                        maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                        disabled={(formValues?.allowedPerson && (formValues?.allowedPerson?.shareWithUsers?.length > 0 || formValues?.allowedPerson?.shareWithGroups?.length > 0)) ? false : true}
                        placeholder={t("Select projects")}
                        options={selectedMasterData}
                      />
                    </Form.Item>

                  )
                }
                {/* <Form.Item name="restrictedUser" valuePropName="checked" initialValue={false} className="">
                  <Checkbox
                    disabled={(formValues?.allowedPerson && (formValues?.allowedPerson?.shareWithUsers?.length > 0 || formValues?.allowedPerson?.shareWithGroups?.length > 0)) ? false : true}
                  >
                    Is a collaborator?
                  </Checkbox>
                </Form.Item> */}

                <Form.Item className="!mb-0">
                  <Button
                    htmlType="submit"
                    disabled={!submittable}
                    
                    icon={<PlusOutlined />}
                    className="w-full lg:w-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {t("Add Member")}
                  </Button>
                </Form.Item>
              </div>
            </Form>
          )}
        </div>

        {/* Members Count Card */}
        <div className="flex flex-row gap-4">
          <div className="bg-gray-50 px-6 py-4 rounded-lg text-center min-w-[120px]">
            <div className="text-3xl font-semibold text-gray-900 leading-none">{statistics.individualUsers}</div>
            <div className="text-sm text-gray-500 mt-1">{t("Members")}</div>
          </div>
          <div className="bg-gray-50 px-6 py-4 rounded-lg text-center min-w-[120px]">
            <div className="text-3xl font-semibold text-gray-900 leading-none">{statistics.groups}</div>
            <div className="text-sm text-gray-500 mt-1">{t("Groups")}</div>
          </div>
          <div className="bg-gray-50 px-6 py-4 rounded-lg text-center min-w-[120px]">
            <div className="text-3xl font-semibold text-gray-900 leading-none">{users.length}</div>
            <div className="text-sm text-gray-500 mt-1">{t("Total Members")}</div>
          </div>
        </div>
      </div>
      {/* Search and Members Table */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <Typography.Text className="text-gray-600 mb-2 sm:mb-0">
            {t("Manage members and their roles in this space")}
          </Typography.Text>
          <Input.Search
            placeholder={t("Search users or groups...")}
            allowClear
            className="w-full sm:w-auto sm:max-w-[300px]"
            onSearch={(value) => setIsSearch(value || false)}
            onChange={(e) => setIsSearch(e.target.value || false)}
          />
        </div>

        <div className="overflow-x-auto">
          <Table
            className="users-table"
            size="small"
            rowKey={(record) => record.groupId || record.userId || record.email}
            dataSource={filteredUsers}
            // Row click opens group members modal
            // onRow={(record) => record.groupId ? ({ onClick: () => openGroupMembers(record) }) : ({})}
            columns={columns}
            pagination={{
              position: ["bottomLeft"],
              showSizeChanger: true,
              size: "small",
              responsive: true,
              className: "custom-pagination",
              current: currentPage,
              pageSize: pageSize,
              total: filteredUsers.length,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t("of")} ${total} ${t("members")}`,
              itemRender: (page, type, originalElement) => {
                if (type === 'prev') {
                  return <Button size="small" type="text">{'<'}</Button>;
                }
                if (type === 'next') {
                  return <Button size="small" type="text">{'>'}</Button>;
                }
                return originalElement;
              }
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>
      {/* Group Members Modal */}
      <Modal
        open={groupMembersModal.open}
        title={t('Assigned Members')}
        onCancel={() => setGroupMembersModal({ open: false, loading: false, members: [], group: null })}
        footer={null}
        width={520}
        destroyOnHidden
      >
        {groupMembersModal.loading ? (
          <div className="flex justify-center py-8"><Spin /></div>
        ) : (
          <div className="space-y-4">
            {groupMembersModal.members.map((m, idx) => (
              <div key={m.userId || m.email || idx} className="flex items-center gap-3">
                <div className="h-6 w-6 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 text-xs font-medium">{idx + 1}</div>
                <div className="flex-1">
                  <div className="text-gray-900 text-sm font-medium">{m.name}</div>
                  <div className="text-gray-500 text-xs">{m.email}</div>
                </div>
              </div>
            ))}
            {groupMembersModal.members.length === 0 && (
              <div className="text-center text-gray-500 py-4 text-sm">{t('No members found')}</div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Sharings;
